import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import moment from "moment";
import DevicesEditorView from "../components/devices-editor-view";
import { meData, meLocale } from "modules/me-module";
import {
  devicesList,
  devicesRequestGet,
  getMapMarkersData,
  getCurrentDevice,
  approveDeviceRequestPut,
  reportDeviceRequestPut,
} from "modules/devices-module";
import { uiShowToastrMessage } from "modules/ui-module";
import RedMarker from "static/images/svg/red-marker.svg";
import BlueMarker from "static/images/svg/blue-marker.svg";
import castleIOWrapper from "lib/castleio-tracking";
import * as Castle from "@castleio/castle-js";
import { useSelector } from "react-redux";
import { stopEvent } from "lib/utility-functions";
import { subdivision } from "iso-3166-2";


export const DevicesEditorContainer = ({ openDevicesDialogOnLoad }) => {
  const { devicesListAll, mapMarkerData, currentDevice, lang, personData } = useSelector(getValuesFromStore);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  let [allValues, setAllValues] = useState({
    errors: {},
    isDirty: false,
    modalIsOpen: false,
    selectedDevice: {},
    clickedDeviceId: "",
    reviewDevicesContentClass: "",
    trustedDeviceControlsClass: "hidden",
    unrecognizedDeviceControlsClass: "hidden",
    devicesModalContentClass: "",
    secureYourAccountModalContentClass: "hidden",
  });
  const [popoverInfo, setPopoverInfo] = useState({
    popoverIsOpen: false,
    deviceId: ""
  });

  const openModal = (e) => {
    stopEvent(e);
    setAllValues((prevState) => ({
      ...prevState,
      modalIsOpen: true,
    }));
  };

  const closeModal = (e) => {
    stopEvent(e);
    setAllValues((prevState) => ({
      ...prevState,
      modalIsOpen: false,
    }));
  };

  useEffect(() => {
    setIsLoading(true);
    if (castleIOWrapper && castleIOWrapper.identifyUserAndTrackPageInCastle) {
      castleIOWrapper.identifyUserAndTrackPageInCastle(
        personData.id,
        personData.userName,
        "Profile Management Portal - Sign In & Security"
      );
    }
    async function fn() {
      return Castle.createRequestToken().then(
        (requestToken) => {
          return dispatch(devicesRequestGet(requestToken)).then(
            () => {
              if (openDevicesDialogOnLoad && !allValues.modalIsOpen) {
                openModal();
              }
              setIsLoading(false);
              return true;
            },
            () => {
              setIsLoading(false);
              return false;
            }
          );
        },
        () => {
          setIsLoading(false);
          return false;
        }
      );
    }
    fn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, devicesRequestGet, openDevicesDialogOnLoad]);
  const displayToastrMessage = (toastrType, toastrMessageId) => {
    dispatch(uiShowToastrMessage(toastrType, toastrMessageId));
  };

  const getDevice = (deviceId) =>
    Object.values(devicesListAll).find((device) => device.fingerprint === deviceId);

  const getDevicesList = () => {

    const currIndex = devicesListAll.findIndex(
      (device) => device.fingerprint === currentDevice.fingerprint
    );

    devicesListAll.push(...devicesListAll.splice(0, currIndex));
    return devicesListAll;
  };

  const getCurrentDeviceParams = (device) => {
    const deviceParams = {
      fingerprint: "",
      os: "",
      deviceType: "",
      browser: "",
      city: "",
      region: "",
      country: "",
      locationResourceId: "devices-editor.unknown-location",
    };
    if (device && device.fingerprint) {
      deviceParams.fingerprint = device.fingerprint;
      deviceParams.os =
        device.os ? device.os : "";
      deviceParams.deviceType = device.deviceType ? device.deviceType : "";
      deviceParams.browser =
        device.browser ? device.browser : "";
      deviceParams.city = device.city || "";
      deviceParams.region = device.region || "";
      deviceParams.country = device.country || "";
      deviceParams.locationResourceId = device.locationResourceId || "";
    }
    return deviceParams;
  };

  const refocusSelectedDevice = () => {
    const token = allValues.selectedDevice.fingerprint;
    if (token) {
      setTimeout(() => {
        const div = document.getElementById(token);
        if (div) {
          const aArray = div.getElementsByTagName("a");
          if (aArray.length) {
            aArray[0].focus();
          } else {
            div.focus();
          }
        }
      }, 100);
    }
  };

  const onDeviceNameClick = (deviceId) => (e) => {
    stopEvent(e);
    const clickedDeviceId = deviceId;
    setAllValues((prevState) => ({
      ...prevState,
      clickedDeviceId,
    }));
  };

  const togglePopover = (deviceId) => (e) => {
    const popOverNewtState = !popoverInfo.popoverIsOpen;

    setPopoverInfo({
      popoverIsOpen: popOverNewtState,
      deviceId
    });
    return true;
  };

  const onTrustDeviceMenuClick = (deviceId) => (e) => {
    stopEvent(e);
    setPopoverInfo({
      popoverIsOpen: false,
      deviceId: ""
    });
    if (deviceId) {
      const trustedDeviceControlsClass = "";
      const reviewDevicesContentClass = "hidden";
      const selectedDevice = getDevice(deviceId);

      setAllValues((prevState) => ({
        ...prevState,
        selectedDevice,
        trustedDeviceControlsClass,
        reviewDevicesContentClass,
      }));
    }
    return false;
  };

  const onTrustDeviceButtonClick = (e) => {
    let castleClientId;
    stopEvent(e);
    if (allValues.selectedDevice.fingerprint) {
      return dispatch(
        approveDeviceRequestPut(
          encodeURIComponent(allValues.selectedDevice.fingerprint)
        )
      )
        .then(
          () => {
            displayToastrMessage(
              "success",
              "devices-editor.trusted-device-save-successful"
            );

            Castle.createRequestToken().then((requestToken) => {
              castleClientId = requestToken;
            });
            if (castleClientId) {
              dispatch(devicesRequestGet(castleClientId));
            }
            refocusSelectedDevice();
          },
          () => {
            displayToastrMessage(
              "error",
              "devices-editor.trusted-device-save-failed"
            );
          }
        )
        .then(() => {
          setAllValues((prevState) => ({
            ...prevState,
            selectedDevice: {},
          }));
          closeTrustDeviceModal();
        });
    }
    return true;
  };

  const closeTrustDeviceModal = (e) => {
    const trustedDeviceControlsClass = "hidden";
    const reviewDevicesContentClass = "";

    setAllValues((prevState) => ({
      ...prevState,
      selectedDevice: {},
      trustedDeviceControlsClass,
      reviewDevicesContentClass,
    }));
    refocusSelectedDevice();
  };

  const closeUnrecognizedDeviceModal = (e) => {
    stopEvent(e);
    const unrecognizedDeviceControlsClass = "hidden";
    const reviewDevicesContentClass = "";

    setAllValues((prevState) => ({
      ...prevState,
      selectedDevice: {},
      unrecognizedDeviceControlsClass,
      reviewDevicesContentClass,
    }));
    refocusSelectedDevice();
  };

  const onUnrecognizedDeviceMenuClick = (deviceId) => (e) => {
    if (deviceId) {
      const unrecognizedDeviceControlsClass = "";
      const reviewDevicesContentClass = "hidden";
      const selectedDevice = getDevice(deviceId);
      setPopoverInfo({
        popoverIsOpen: false,
        deviceId: ""
      });
      setAllValues((prevState) => ({
        ...prevState,
        selectedDevice,
        unrecognizedDeviceControlsClass,
        reviewDevicesContentClass,
      }));
    }
    return false;
  };

  const onReportDeviceButtonClick = (e) => {
    if (allValues.selectedDevice.fingerprint) {
      return dispatch(
        reportDeviceRequestPut(
          encodeURIComponent(allValues.selectedDevice.fingerprint)
        )
      ).then(
        () => {
          displayToastrMessage(
            "success",
            "devices-editor.report-device-save-successful"
          );
          const devicesModalContentClass = "hidden";
          const secureYourAccountModalContentClass = "";
          const unrecognizedDeviceControlsClass = "hidden";

          setAllValues((prevState) => ({
            ...prevState,
            devicesModalContentClass,
            secureYourAccountModalContentClass,
            unrecognizedDeviceControlsClass
          }));
          refocusSelectedDevice();
        },
        () => {
          displayToastrMessage(
            "error",
            "devices-editor.report-device-save-failed"
          );
        }
      );
    }
    return true;
  };

  const markerClick = (props, marker, e) => {
    if (e && e.xa) {
      e.xa.stopPropagation();
      e.xa.preventDefault();
    }
    const clickedDeviceId = marker.name;

    setAllValues((prevState) => ({
      ...prevState,
      clickedDeviceId,
    }));
    const container = document.getElementById("deviceList");
    const deviceRow = document.getElementById(clickedDeviceId);
    if (container && deviceRow) {
      container.scrollTop = 0; // move to 0 pixels scroll position, then
      container.scrollTop = deviceRow.offsetTop - container.offsetTop; // calculate new scrollTop value
    }
  };

  return (
    <DevicesEditorView
      onDeviceEditLinkClick={openModal}
      modalIsOpen={allValues.modalIsOpen}
      closeModal={closeModal}
      isLoading={isLoading}
      closeTrustDeviceModal={closeTrustDeviceModal}
      closeUnrecognizedDeviceModal={closeUnrecognizedDeviceModal}
      errors={allValues.errors}
      devicesListDisplay={getDevicesList()}
      mapMarkerData={mapMarkerData}
      markerClick={markerClick}
      clickedDeviceId={allValues.clickedDeviceId}
      currentDeviceParams={getCurrentDeviceParams(currentDevice)}
      onTrustDeviceMenuClick={onTrustDeviceMenuClick}
      onTrustDeviceButtonClick={onTrustDeviceButtonClick}
      onUnrecognizedDeviceMenuClick={onUnrecognizedDeviceMenuClick}
      onReportDeviceButtonClick={onReportDeviceButtonClick}
      onDeviceNameClick={onDeviceNameClick}
      trustedDeviceControlsClass={allValues.trustedDeviceControlsClass}
      reviewDevicesContentClass={allValues.reviewDevicesContentClass}
      unrecognizedDeviceControlsClass={allValues.unrecognizedDeviceControlsClass}
      devicesModalContentClass={allValues.devicesModalContentClass}
      secureYourAccountModalContentClass={allValues.secureYourAccountModalContentClass}
      locale={lang}
      selectedDevice={allValues.selectedDevice}
      userEmail={personData.userName || ""}
      selectedDeviceTitle={allValues.selectedDevice.title || ""}
      popoverIsOpen={popoverInfo.popoverIsOpen || false}
      togglePopoverView={togglePopover}
      popoverOpendDeviceId={popoverInfo.deviceId || ""}
    />
  );
};

DevicesEditorContainer.propTypes = {
  openDevicesDialogOnLoad: PropTypes.bool,
};

export const getDurationSinceLastLogin = (device) => {
  const retVal = { count: 0, resourceRef: "device-detail.duration-unknown" };
  if (device.lastSeen && device.lastSeen.toLowerCase() !== "unknown") {
    const now = moment(new Date());
    const end = moment(device.lastSeen);
    const timeSinceLastLogin = moment.duration(now.diff(end));
    const asMonths = Math.floor(timeSinceLastLogin.asMonths());
    const asDays = Math.floor(timeSinceLastLogin.asDays());
    const asHours = Math.floor(timeSinceLastLogin.asHours());
    const asMinutes = Math.floor(timeSinceLastLogin.asMinutes());

    if (asMonths > 0) {
      retVal.count = asMonths;
      retVal.resourceRef =
        asMonths > 1
          ? "device-detail.duration-months"
          : "device-detail.duration-month";
    } else if (asDays > 0) {
      retVal.count = asDays;
      retVal.resourceRef =
        asDays > 1
          ? "device-detail.duration-days"
          : "device-detail.duration-day";
    } else if (asHours > 0) {
      retVal.count = asHours;
      retVal.resourceRef =
        asHours > 1
          ? "device-detail.duration-hours"
          : "device-detail.duration-hour";
    } else if (asMinutes > 0) {
      retVal.count = asMinutes;
      retVal.resourceRef =
        asMinutes > 1
          ? "device-detail.duration-minutes"
          : "device-detail.duration-minute";
    } else {
      retVal.count = 0;
      retVal.resourceRef = "device-detail.duration-unknown";
    }
  }
  return retVal;
};

const getTimeOfLastLogin = (locale, device) => {
  let retVal = "";
  if (device.lastSeen && device.lastSeen.toLowerCase() !== "unknown") {
    const lang = locale.length ? locale.split("_")[0] : "en";
    retVal = moment(device.lastSeen).locale(lang).format("LLLL");
  }
  return retVal;
};

const getApprovedDate = (locale, device) => {
  if (device.trusted && device.trusted.toLowerCase() !== "unknown") {
    const lang = locale.length ? locale.split("_")[0] : "en";
    return moment(device.trusted).locale(lang).format("ll");
  }
  return "";
};

const getDeviceLocation = (device) => {
  const deviceLocation = {
    country: "",
    region: "",
    city: "",
  };
  if (!device.location) {
    return deviceLocation;
  } else {
    const deviceLocationInfo = device.location;
    if ( typeof deviceLocationInfo === 'string') {  //already converted to a string, send it back
      return deviceLocationInfo;
    }
    //otherwise, process it into an object, then return it
    deviceLocation.city = deviceLocationInfo.city || "";
    const locationCountry = deviceLocationInfo.countryCode && deviceLocationInfo.countryCode.length === 2 ? deviceLocationInfo.countryCode : '';
    const locationRegion = deviceLocationInfo.regionCode && deviceLocationInfo.regionCode.length === 2 ? deviceLocationInfo.regionCode : '';
    let iso3166Location =  subdivision(locationCountry, locationRegion);
    deviceLocation.country = iso3166Location?.countryName || "";
    deviceLocation.region = iso3166Location?.name || "";
    return deviceLocation;
  }
};
const getLocationResourceId = (device) => {
  const deviceLocation = getDeviceLocation(device);
  let retVal = "";
  if (!deviceLocation.country && !deviceLocation.region && !deviceLocation.city) {
    retVal = "devices-editor.unknown-location";
  } else if (!deviceLocation.region) {
    retVal = "devices-editor.location-no-region";
  } else {
    retVal = "devices-editor.location";
  }
  return retVal;
};
export const getValuesFromStore = (state) => {
  /* eslint no-param-reassign: ["error", { "props": false }] */
  const lang = meLocale(state);
  const markerData = getMapMarkersData(state);
  markerData.forEach((marker) => {
    switch (marker.trustLevel) {
      case 1:
        marker.iconRef = RedMarker;
        break;
      case 3:
        marker.iconRef = BlueMarker;
        break;
      default:
        marker.iconRef = BlueMarker;
    }
    return marker;
  });

  const devices = devicesList(state);
  devices.forEach((device) => {
    const deviceLocation = getDeviceLocation(device);
    device.os = typeof device.os === 'string'
      ? device.os
      : device.os?.name ? `${device.os.name}${device.os.fullVersion ? ' ' + device.os.fullVersion : ''}` : '';

    device.browser = typeof device.browser === 'string'
      ? device.browser
      : device.browser?.name || '';

    const { hardware, ipAddress } = device;
    device.deviceType = hardware?.type || '';

    // Only assign city, region, country and locationResourceId if they are not already set
    if (!device.city) device.city = deviceLocation.city || '';
    if (!device.region) device.region = deviceLocation.region || '';
    if (!device.country) device.country = deviceLocation.country || '';
    if (!device.locationResourceId) device.locationResourceId = getLocationResourceId(device);

    device.timeSinceLastLogin = getDurationSinceLastLogin(device);
    device.timeOfLastLogin = getTimeOfLastLogin(lang, device);
    device.trustedDate = getApprovedDate(lang, device);
    device.ip = ipAddress || '';
  });

  return {
    devicesListAll: devices,
    mapMarkerData: markerData,
    currentDevice: getCurrentDevice(state),
    lang,
    personData: meData(state),
  };
};
export default DevicesEditorContainer;
